<template>
  <div class="content-wrapper view-aptlist-manage p-0">
    <div class="content">
      <div class="card">
        <div class="card-body">
          <!-- Header -->
          <div class="d-flex align-items-center justify-content-between mb-3">
            <div class="d-flex align-items-center gap-2">
              <b-button
                variant="outline"
                size="sm"
                @click="handleBack"
                v-b-tooltip.hover
                title="ย้อนกลับ"
              >
                <i
                  class="fas fa-chevron-left"
                  style="font-size: 20px"
                ></i>
              </b-button>
              <h4 class="mb-0">การจัดการสต๊อก</h4>
              <AdjustmentStatusBadge
                v-if="stockAdjustment"
                :status="stockAdjustment.normalizedStatus"
              />
            </div>
            <div
              v-if="
                stockAdjustment &&
                stockAdjustment.normalizedStatus ===
                  ADJUSTMENT_STATUS.WAITING_BM
              "
              class="d-flex gap-4"
            >
              <b-button
                variant="outline-primary"
                size="sm"
                class="btn-edit-adj"
                @click="openStockAdjustmentEditModal"
              >
                แก้ไขรายการ
              </b-button>
              <b-button
                v-if="stockAdjustment.rawType === STOCK_TYPE_NAME.PHYSICAL_COUNT"
                variant="outline-primary"
                size="sm"
                class="btn-compare-phy"
                @click="triggerCompareFileInput"
              >
                เปรียบเทียบสต๊อกจากไฟล์
              </b-button>
              <b-button
                variant="outline-danger"
                size="sm"
                class="btn-delete-adj"
                @click="handleDeleteAdjustment"
              >
                ยกเลิกรายการ
              </b-button>
              <input
                ref="compareFileInput"
                type="file"
                accept=".xlsx,.xls"
                style="display: none"
                @change="handleCompareFile"
              />
              <b-button
                v-if="isStockManager"
                variant="primary"
                size="sm"
                class="btn-approve-adj"
                @click="handleApprove"
              >
                อนุมัติ
              </b-button>
            </div>
          </div>

          <!-- Two-column layout -->
          <div
            v-if="stockAdjustment"
            class="stock-management-detail-layout"
          >
            <!-- Left: metadata -->
            <div class="stock-management-detail-info">
              <div class="info-row">
                <span class="info-label">รหัส:</span>
                <span class="info-value">{{
                  stockAdjustment.adjustmentNo || "-"
                }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">ประเภทการจัดการ:</span>
                <span class="info-value">{{
                  stockAdjustment.type || "-"
                }}</span>
              </div>
              <div
                v-if="
                  stockAdjustment.rawType === 'RETURN' &&
                  stockAdjustment.refDocNo
                "
                class="info-row info-row-ref"
              >
                <span class="info-label">รหัสใบรับสินค้าอ้างอิง:</span>
                <span class="info-value">
                  {{ stockAdjustment.refDocNo }}
                  <i
                    class="fas fa-info-circle info-ref-icon"
                    @click="goToGRDetail"
                    v-b-tooltip.hover
                    title="ดูรายละเอียดใบรับสินค้า"
                  ></i>
                </span>
              </div>
              <div class="info-row">
                <span class="info-label">เหตุผล:</span>
                <span class="info-value">{{
                  stockAdjustment.reason || "-"
                }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">ชื่อผู้ทำรายการ:</span>
                <span class="info-value">{{
                  stockAdjustment.createdByName || "-"
                }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">ต้องได้รับการอนุมัติจาก:</span>
                <span class="info-value">
                  <ApprovalSteps :show-hq="needsHqApprove" />
                </span>
              </div>
              <div class="info-row">
                <span class="info-label">วันที่มีผล:</span>
                <span class="info-value">{{
                  stockAdjustment.transactionDate
                    ? formatDate(stockAdjustment.transactionDate)
                    : "-"
                }}</span>
              </div>
              <div
                v-if="
                  stockAdjustment.rawType === STOCK_TYPE_NAME.PHYSICAL_COUNT &&
                  stockAdjustment.countedDt
                "
                class="info-row"
              >
                <span class="info-label">วันที่นับจริง:</span>
                <span class="info-value">{{
                  formatDate(stockAdjustment.countedDt)
                }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">วันที่ทำรายการ:</span>
                <span class="info-value">{{
                  stockAdjustment.createdDate
                    ? formatDate(stockAdjustment.createdDate)
                    : "-"
                }}</span>
              </div>
              <div
                v-if="showRejectReason"
                class="reject-reason-box"
              >
                <div class="reject-reason-title">เหตุผลที่ถูกปฏิเสธ:</div>
                <div>{{ stockAdjustment.rejectReason }}</div>
              </div>
            </div>

            <!-- Right: product table -->
            <div class="stock-management-detail-table">
              <b-table
                id="stockManagementDetailProductTable"
                hover
                small
                responsive
                head-variant="light"
                :items="stockAdjustment.products || []"
                :fields="productFields"
              >
                <template #head(totalQty)>
                  <div class="text-right">
                    <div>จำนวนสต๊อก</div>
                    <div v-if="stockAdjustment?.transactionDate">
                      {{ formatDate(stockAdjustment.transactionDate) }}
                    </div>
                  </div>
                </template>
                <template v-slot:cell(index)="row"
                  >{{ row.index + 1 }}.</template
                >
                <template v-slot:cell(image)="row">
                  <ProductImage :src="row.item.imageUrl || null" />
                </template>
                <template v-slot:cell(productNo)="row">
                  {{ row.item.no || row.item.productNo || "-" }}
                </template>
                <template v-slot:cell(productName)="row">
                  {{ row.item.description || "-" }}
                </template>
                <template v-slot:cell(adjustmentQty)="row">
                  {{ adjustmentQtyDisplay(row.item) }}
                </template>
                <template v-slot:cell(totalQty)="row">
                  {{ formatTotalQty(row.item.totalQty) }}
                </template>
                <template v-slot:cell(uom)="row">
                  {{ uomDisplay(row.item) }}
                </template>
              </b-table>
            </div>
          </div>
        </div>
      </div>

      <HQApprovalActionModal
        ref="actionModal"
        :needs-hq-approve="needsHqApprove"
        :show-older-pending-warning="
          stockAdjustment && hasOlderPendingAdjustments
        "
        :is-older-physical-count-pending="hasOlderPhysicalCountPending"
        @approve="confirmApprove"
      />

      <CompareStockFromFileModal
        ref="compareModal"
        :items="compareItems"
      />

      <Dialog ref="Dialog"></Dialog>
      <Loading v-if="isLoading"></Loading>
      <StockAdjustmentModal
        ref="stockAdjustmentModal"
        @stock-adjusted="loadData"
      ></StockAdjustmentModal>
    </div>
  </div>
</template>

<script>
import Loading from "@/components/Loading";
import Dialog from "@/components/modal/Dialog.vue";
import ProductImage from "@/components/table/stock/component/ProductImage.vue";
import StockAdjustmentModal from "@/components/modal/ProductAndStock/StockAdjustment.vue";
import HQApprovalActionModal from "@/components/ProductAndStock/HQApprovalActionModal.vue";
import CompareStockFromFileModal from "@/components/modal/ProductAndStock/CompareStockFromFileModal.vue";
import { ROLES } from "@/constants/user";
import { ADJUSTMENT_STATUS, STOCK_TYPE_NAME } from "@/constants/productStock";
import AdjustmentStatusBadge from "@/components/badge/ProductAndStock/AdjustmentStatusBadge.vue";
import ApprovalSteps from "@/components/badge/ProductAndStock/ApprovalSteps.vue";
import { mapActions, mapGetters } from "vuex";
import { formatDate } from "../../utils/utils";
import { formatUomWithBase } from "@/utils/uomConversion";

export default {
  name: "StockManagementDetail",
  components: {
    Dialog,
    Loading,
    ProductImage,
    StockAdjustmentModal,
    AdjustmentStatusBadge,
    ApprovalSteps,
    HQApprovalActionModal,
    CompareStockFromFileModal,
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fromRoute = from;
    });
  },
  data() {
    return {
      ROLES,
      ADJUSTMENT_STATUS,
      STOCK_TYPE_NAME,
      isLoading: false,
      stockAdjustment: null,
      rawStockAdjustment: null,
      fromRoute: null,
      compareItems: [],
    };
  },
  async mounted() {
    await this.loadData();
  },
  computed: {
    ...mapGetters({
      productList: "moduleProductInventory/getProductList",
      getStockAdjustmentList: "moduleProductInventory/getStockAdjustmentList",
      getProductUnitList: "moduleUnitMaster/getUnitList",
      isAccessMenu: "moduleUser/getIsAccessMenu",
    }),
    isStockManager() {
      return this.isAccessMenu([ROLES.ROLE_BRANCH_STOCK_MANAGER]);
    },
    hasOlderNonPhysicalCountPending() {
      if (!this.stockAdjustment?.transactionDate) return false;
      return (this.getStockAdjustmentList || []).some(item => {
        if (String(item.id) === String(this.stockAdjustment.id)) return false;
        if (
          this.normalizeAdjStatus(item.status) !== ADJUSTMENT_STATUS.WAITING_BM
        )
          return false;
        if (item.type === STOCK_TYPE_NAME.PHYSICAL_COUNT) return false;
        return item.transactionDt < this.stockAdjustment.transactionDate;
      });
    },
    hasOlderPhysicalCountPending() {
      if (!this.stockAdjustment?.transactionDate) return false;
      return (this.getStockAdjustmentList || []).some(item => {
        if (String(item.id) === String(this.stockAdjustment.id)) return false;
        if (
          this.normalizeAdjStatus(item.status) !== ADJUSTMENT_STATUS.WAITING_BM
        )
          return false;
        if (item.type !== STOCK_TYPE_NAME.PHYSICAL_COUNT) return false;
        return item.transactionDt < this.stockAdjustment.transactionDate;
      });
    },
    hasOlderPendingAdjustments() {
      return (
        this.hasOlderNonPhysicalCountPending ||
        this.hasOlderPhysicalCountPending
      );
    },
    showRejectReason() {
      return (
        this.stockAdjustment?.normalizedStatus === ADJUSTMENT_STATUS.REJECTED &&
        !!this.stockAdjustment?.rejectReason
      );
    },
    needsHqApprove() {
      if (!this.stockAdjustment) return false;
      if (this.stockAdjustment.needHqApproval) return true;
      const { rawType, transactionDate, createdDate } = this.stockAdjustment;
      if (rawType === STOCK_TYPE_NAME.GENERAL || rawType === STOCK_TYPE_NAME.PHYSICAL_COUNT) return true;
      const tDate = transactionDate
        ? new Date(transactionDate).toDateString()
        : null;
      const cDate = createdDate ? new Date(createdDate).toDateString() : null;
      return tDate !== cDate;
    },
    productFields() {
      const fields = [
        { key: "index", label: "", thStyle: { width: "40px" } },
        {
          key: "image",
          label: "",
          thStyle: { width: "50px", minWidth: "50px" },
        },
        { key: "productNo", label: "รหัส", thStyle: { minWidth: "100px" } },
        {
          key: "productName",
          label: "ชื่อสินค้า",
          thStyle: { minWidth: "160px" },
        },
        {
          key: "adjustmentQty",
          label: "จำนวนปรับปรุง",
          thStyle: { minWidth: "100px" },
          class: "text-right",
        },
      ];

      if (this.stockAdjustment?.rawType === STOCK_TYPE_NAME.PHYSICAL_COUNT) {
        fields.push({
          key: "totalQty",
          label: "จำนวนสต๊อก",
          thStyle: { minWidth: "100px" },
          class: "text-right",
        });
      }

      fields.push({
        key: "uom",
        label: "หน่วยสินค้า",
        thStyle: { minWidth: "100px" },
      });

      return fields;
    },
  },
  methods: {
    ...mapActions({
      fetchStockAdjustmentById:
        "moduleProductInventory/fetchStockAdjustmentById",
      deleteStockAdjustmentAction:
        "moduleProductInventory/deleteStockAdjustment",
      approveStockAdjustmentAction:
        "moduleProductInventory/approveStockAdjustment",
      fetchUnitList: "moduleUnitMaster/fetchUnitList",
    }),
    goToGRDetail() {
      const grId = this.stockAdjustment?.refDocId;
      if (!grId) {
        this.showDialogToast("danger", "ไม่พบใบรับสินค้าที่อ้างอิง");
        return;
      }
      const { clinicUrl, branchUrl } = this.$route.params;
      this.$router.push({
        name: "GoodsReceiveDetail",
        params: { clinicUrl, branchUrl, grId },
      });
    },
    async loadData() {
      this.isLoading = true;
      try {
        const { clinicUrl, branchUrl, adjustmentId } = this.$route.params;

        const res = await this.fetchStockAdjustmentById({
          clinicUrl,
          branchUrl,
          adjustmentId,
        });

        this.rawStockAdjustment = res;

        if (!res) {
          this.$refs.Dialog.showToast(
            "danger",
            "รายการนี้ถูกลบไปแล้ว กรุณารีเฟรชหน้าเพื่ออัปเดตข้อมูล"
          );
          setTimeout(() => {
            this.goBack();
          }, 1500);
          return;
        }

        const createdByName = [
          res.createdBy?.firstNameTh,
          res.createdBy?.lastNameTh,
        ]
          .filter(Boolean)
          .join(" ");

        const listItem = (this.getStockAdjustmentList || []).find(
          i => String(i.id) === String(res.id)
        );
        const resolvedStatus = res.status || listItem?.status;

        this.stockAdjustment = {
          id: res.id,
          adjustmentNo: res.adjustmentNo,
          type: this.getAdjustmentDetail(res.type),
          rawType: res.type,
          refDocNo: res.refDocNo || null,
          refDocId: res.refDocId || null,
          reason: res.reason,
          status: resolvedStatus,
          normalizedStatus: this.normalizeAdjStatus(resolvedStatus),
          syncDt: res.syncDt,
          createdBy: res.createdBy || null,
          createdByName: createdByName || "-",
          createdDate: res.createdDate,
          transactionDate: res.transactionDate,
          countedDt: res.countedDt,
          needHqApproval: Boolean(res.needHqApproval),
          rejectReason: res.rejectReason || null,
          products: (res.stockLedgers || []).map(item => ({
            productId: item.productId,
            no: item.product?.no || "-",
            description: item.product?.description || "-",
            description2: item.product?.description2 || "",
            imageUrl:
              this.productList.find(p => p.productId === item.productId)
                ?.imageUrl || null,
            adjustmentQty: item.qtyUom,
            qtyBaseUom: item.qtyBaseUom ?? null,
            totalQty: item.totalQty,
            uom: item.uom || null,
            uomName: item.uom?.description || "-",
            baseUomName: item.product?.baseUom?.description || "",
          })),
        };
      } catch (err) {
        const errorMessage = err.response?.data?.errorMessage;
        let errorMsg =
          typeof errorMessage === "string"
            ? errorMessage
            : "โหลดข้อมูลไม่สำเร็จ กรุณาลองใหม่";

        if (
          errorMsg === "ไม่พบข้อมูลการปรับปรุงสต๊อก" ||
          err.response?.status === 404
        ) {
          errorMsg = "รายการนี้ถูกลบไปแล้ว กรุณารีเฟรชหน้าเพื่ออัปเดตข้อมูล";
        }

        this.$refs.Dialog.showToast("danger", errorMsg);

        if (
          errorMsg === "รายการนี้ถูกลบไปแล้ว กรุณารีเฟรชหน้าเพื่ออัปเดตข้อมูล"
        ) {
          setTimeout(() => {
            this.goBack();
          }, 1500);
        } else {
          this.goBack();
        }
        return;
      } finally {
        this.isLoading = false;
      }
    },
    async openStockAdjustmentEditModal() {
      try {
        this.isLoading = true;
        const { clinicUrl, branchUrl } = this.$route.params;

        const adjustmentDetail = this.rawStockAdjustment;

        if (!adjustmentDetail) {
          return;
        }

        if (!this.getProductUnitList || this.getProductUnitList.length === 0) {
          await this.fetchUnitList({
            clinicUrl,
            branchUrl,
          });
        }

        this.$refs.stockAdjustmentModal.show({
          unitList: this.getProductUnitList,
          editData: adjustmentDetail,
        });
      } catch (err) {
        console.error("Failed to prepare modal for edit:", err);
        this.$refs.Dialog.showToast("danger", "เกิดข้อผิดพลาด กรุณาลองใหม่");
      } finally {
        this.isLoading = false;
      }
    },
    normalizeAdjStatus(status) {
      const remap = { PENDING: "WAITING_BM", CONFIRMED: "COMPLETED" };
      return remap[status] || status || "";
    },
    showDialogToast(variant, textDetail) {
      this.$refs.Dialog.showToast(variant, textDetail);
    },
    async handleDeleteAdjustment() {
      const result = await this.$refs.Dialog.showAlertConfirm(
        "ยกเลิกการจัดการสต๊อก ?",
        "คุณต้องการยกเลิกการจัดการสต๊อกนี้ใช่หรือไม่"
      );
      if (!result.value) return;
      this.isLoading = true;
      try {
        const { clinicUrl, branchUrl, adjustmentId } = this.$route.params;
        await this.deleteStockAdjustmentAction({
          clinicUrl,
          branchUrl,
          adjustmentId,
        });
        this.showDialogToast("success", "ยกเลิกการจัดการสต๊อกสำเร็จ");
        setTimeout(() => this.goBack(), 1000);
      } catch (err) {
        const errorMessage = err.response?.data?.errorMessage;
        this.showDialogToast(
          "danger",
          typeof errorMessage === "string"
            ? errorMessage
            : "ไม่สามารถยกเลิกการจัดการสต๊อกได้ กรุณาลองใหม่"
        );
      } finally {
        this.isLoading = false;
      }
    },
    handleApprove() {
      this.$refs.actionModal.showApprove();
    },
    async confirmApprove() {
      this.isLoading = true;
      try {
        const { clinicUrl, branchUrl, adjustmentId } = this.$route.params;
        const res = await this.approveStockAdjustmentAction({
          clinicUrl,
          branchUrl,
          adjustmentId,
        });
        this.showDialogToast("success", "อนุมัติการจัดการสต๊อกสำเร็จ");

        const updatedData = res?.data?.data;
        if (updatedData?.status) {
          this.stockAdjustment = {
            ...this.stockAdjustment,
            status: updatedData.status,
            normalizedStatus: this.normalizeAdjStatus(updatedData.status),
          };
        }
      } catch (err) {
        const errorMessage = err.response?.data?.errorMessage;
        this.showDialogToast("danger", "อนุมัติการจัดการสต๊อกไม่สำเร็จ กรุณาลองใหม่");
      } finally {
        this.isLoading = false;
      }
    },
    formatDate(value) {
      return formatDate(value);
    },
    getAdjustmentDetail(type) {
      switch (type) {
        case STOCK_TYPE_NAME.GENERAL:
          return "ปรับปรุงทั่วไป";
        case STOCK_TYPE_NAME.RETURN:
          return "ส่งคืนสินค้า";
        case STOCK_TYPE_NAME.PHYSICAL_COUNT:
          return "ตรวจนับสต๊อก";
        case STOCK_TYPE_NAME.WITHDRAW:
          return "ตัดเบิกของ";
        default:
          return type || "-";
      }
    },
    formatAdjustQty(value) {
      if (value === null || value === undefined || value === "") return "-";
      const num = Number(value);
      if (Number.isNaN(num)) return String(value);
      const formatted = num.toLocaleString("en-US");
      if (num > 0) return `+${formatted}`;
      return formatted;
    },
    adjustmentQtyDisplay(item) {
      const main = this.formatAdjustQty(item.adjustmentQty);
      if (item.qtyBaseUom == null) return main;
      return `${main}(${this.formatAdjustQty(item.qtyBaseUom)})`;
    },
    uomDisplay(item) {
      return formatUomWithBase(
        (item.uom && item.uom.description) || item.uomName,
        item.baseUomName
      );
    },
    formatTotalQty(value) {
      if (value === null || value === undefined || value === "") return "-";
      const num = Number(value);
      if (Number.isNaN(num)) return String(value);
      return num.toLocaleString("en-US");
    },
    triggerCompareFileInput() {
      this.$refs.compareFileInput.click();
    },
    async handleCompareFile(event) {
      const file = event.target.files[0];
      this.$refs.compareFileInput.value = "";
      if (!file) return;

      try {
        const XLSX = await import("xlsx");
        const buffer = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = e => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsArrayBuffer(file);
        });

        const workbook = XLSX.read(buffer, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const rawRows = XLSX.utils.sheet_to_json(worksheet);

        const requiredCols = ["Product ID", "จำนวนสต๊อกหลังปรับปรุง"];
        const isValid = rawRows?.length && requiredCols.every(col => col in rawRows[0]);
        if (!isValid) {
          this.$refs.Dialog.showToast("danger", "รูปแบบไฟล์ไม่ถูกต้อง กรุณาตรวจสอบและนำเข้าใหม่อีกครั้ง");
          return;
        }

        const fileMap = {};
        rawRows.forEach(row => {
          const code = String(row["รหัสสินค้า"] || "").trim();
          if (code) fileMap[code] = row["จำนวนสต๊อกหลังปรับปรุง"];
        });

        const products = this.stockAdjustment?.products || [];
        const systemCodes = new Set(products.map(p => String(p.no || p.productNo || "").trim()));

        const mapped = products.map(p => {
          const key = String(p.no || p.productNo || "").trim();
          const inFile = key !== "" && key in fileMap;
          const rawFileQty = inFile ? fileMap[key] : null;
          let type;
          if (!inFile) {
            type = "not_in_file";
          } else if (Number(rawFileQty) !== Number(p.totalQty)) {
            type = "mismatch";
          } else {
            type = "match";
          }
          return {
            imageUrl: p.imageUrl || null,
            productNo: p.no || p.productNo || "-",
            productName: p.description || "-",
            adjustmentQty: p.adjustmentQty,
            totalQty: p.totalQty,
            fileQty: inFile ? rawFileQty : "ไม่มีสินค้าในไฟล์",
            uomName: (p.uom && p.uom.description) || p.uomName || "-",
            _type: type,
          };
        });

        const extraRows = rawRows
          .filter(row => {
            const code = String(row["รหัสสินค้า"] || "").trim();
            return code && !systemCodes.has(code);
          })
          .map(row => ({
            imageUrl: null,
            productNo: row["รหัสสินค้า"] || "-",
            productName: row["ชื่อสินค้า"] || "-",
            adjustmentQty: null,
            totalQty: "ไม่มีสินค้าในระบบ",
            fileQty: row["จำนวนสต๊อกหลังปรับปรุง"],
            uomName: row["หน่วยสินค้า"] || "-",
            _type: "not_in_system",
          }));

        const typeOrder = { not_in_system: 0, not_in_file: 1, mismatch: 2, match: 3 };
        this.compareItems = [...extraRows, ...mapped].sort(
          (a, b) => typeOrder[a._type] - typeOrder[b._type]
        );

        this.$refs.compareModal.show();
      } catch (err) {
        console.error("Compare file error:", err);
        this.$refs.Dialog.showToast("danger", "อ่านไฟล์ไม่สำเร็จ กรุณาตรวจสอบรูปแบบไฟล์");
      }
    },
    handleBack() {
      if (this.fromRoute?.name) {
        this.$router.go(-1);
      } else {
        this.goBack();
      }
    },
    goBack() {
      this.$router.push({
        name: "StockManagement",
        params: {
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
        },
      });
    },
  },
};
</script>

<style scoped>
h4 {
  margin: 0;
}

.gap-2 {
  gap: 8px;
}

.gap-4 {
  gap: 16px;
}

.stock-management-detail-layout {
  display: flex;
  gap: 24px;
  border: 1px solid #dee2e6;
  padding: 20px;
}

.stock-management-detail-info {
  flex: 0 0 30%;
  min-width: 0;
  border-right: 1px solid #dee2e6;
  padding-right: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.stock-management-detail-table {
  flex: 1;
  min-width: 0;
  overflow-x: auto;
}

.border-bottom {
  border-bottom: 1px solid #dee2e6;
}

.info-row {
  line-height: 1.6;
  display: flex;
}

.info-row-ref {
  align-items: center;
}

.info-row-ref .info-value {
  display: flex;
  align-items: center;
  gap: 6px;
}

.info-label {
  font-weight: 600;
  color: #000;
  white-space: nowrap;
  flex-shrink: 0;
}

.info-value {
  margin-left: 4px;
  word-break: break-word;
  min-width: 0;
  flex: 1;
}

.info-ref-icon {
  color: #a3a4a6;
  cursor: pointer;
  font-size: 1.1rem;
  transition: color 0.15s;
}

.info-ref-icon:hover {
  color: #1976d2;
}

.product-img {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
}

.product-img-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: #f0f0f0;
  border-radius: 4px;
  color: #aaa;
}

.outline-btn {
  border: 1px solid #5299d6;
  color: #5299d6;
  background: transparent;
  transition: all 0.2s ease;
}

.outline-btn:hover {
  background-color: #5299d6;
  color: #fff;
}

.btn-compare-phy {
  padding: 8px 30px;
  font-size: 14px;
  font-weight: 600;
  border-color: #5299d6;
  color: #5299d6;
}

.btn-compare-phy:hover {
  background-color: #5299d6;
  color: #fff;
}

.btn-edit-adj {
  padding: 8px 30px;
  font-size: 14px;
  font-weight: 600;
  border-color: #5299d6;
  color: #5299d6;
}

.btn-edit-adj:hover {
  background-color: #5299d6;
  color: #fff;
}

.btn-delete-adj {
  padding: 8px 30px;
  font-size: 14px;
  font-weight: 600;
  border-color: #dd3333;
  color: #dd3333;
}

.btn-delete-adj:hover {
  background-color: #dd3333;
  color: #fff;
}

.btn-approve-adj {
  padding: 8px 50px;
  font-size: 14px;
  font-weight: 600;
}

.reject-reason-box {
  background: #ffd9dd;
  border-radius: 4px;
  padding: 16px;
}

.reject-reason-title {
  font-weight: 600;
  font-size: 16px;
  color: #dd3333;
  margin-bottom: 4px;
}

::v-deep #stockManagementDetailProductTable thead th,
::v-deep #stockManagementDetailProductTable tbody td {
  vertical-align: middle !important;
}

::v-deep #stockManagementDetailProductTable tbody tr:last-child td {
  border-bottom: 1px solid #dee2e6 !important;
}

@media (max-width: 768px) {
  .stock-management-detail-layout {
    flex-direction: column;
  }

  .stock-management-detail-info {
    flex: none;
    border-right: none;
    padding-right: 0;
    border-bottom: 1px solid #dee2e6;
    padding-bottom: 16px;
  }
}
</style>
